// src/features/system/developer/components/video-gallery/VideoGalleryPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Zero-prop top-level panel for the Videos surface.
//
// Two coexisting view modes (YT/IG/TikTok parity):
//   • 'feed'    — TikTok/Shorts/Reels-style immersive vertical-snap player.
//                 Full-bleed black, NO visible tab strip — switcher lives
//                 as a header dropdown so nothing competes with content.
//   • 'gallery' — YouTube/IG-grid browser. Visible tab strip at the top so
//                 the structure is obvious, responsive day-grouped grid.
//
// Persistence: the last-used mode is stored in localStorage (see
// `viewMode.ts`) so the surface restores the user's preferred layout next
// visit — same pattern as YouTube Studio's list/grid memory.
//
// Keyboard: `f` → feed, `g` → gallery (ignored while typing in inputs).
//
// Single source of truth for cross-view concerns (delete modal, error
// toast, confirm dialog) — both views share them and they live here.
// ────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VideoFeed from './VideoFeed';
import VideoGridView from './VideoGridView';
import BucketChips from './BucketChips';
import CalendarJumpButton from './CalendarJumpButton';
import type { ReconcileSweepResponse, PurgeOrphansResponse } from './api';
import { BACKFILL_SIDECARS_CONFIRMATION, PURGE_ORPHANS_CONFIRMATION } from './api';
import { useVideoGallery } from './useVideoGallery';
import { formatBytes, formatDuration, formatTimeAgo, statusTone } from './format';
import type { BackfillSidecarsResponse, VideoFeedItem } from './types';
import { type VideoViewMode, readViewMode, writeViewMode } from './viewMode';
import ShareLinkButton from './ShareLinkButton';

function isAuthErrorMessage(msg: string): boolean {
  return /session expired/i.test(msg) || /unauthenticated/i.test(msg) || /401/.test(msg);
}

export interface VideoGalleryPanelProps {
  /**
   * Opaque shared-link token. When provided, every API call is routed
   * through `/bff/shared/:token/*` and write-side affordances (delete,
   * reconcile, share) are hidden. This is the read-only public-viewer
   * entry point used by the `/s/:token` shared-scope renderer.
   */
  token?:    string;
  /** Force the panel into read-only mode (implied true when `token`). */
  readOnly?: boolean;
  /**
   * When set, the panel pins the visible feed to a single video (used
   * by the per-video shared-link viewer at `/s/:token` where
   * `meta.scope_key = "video:<videoId>"`). The header chrome stays
   * unchanged structurally but the body filters down to the one row
   * matching this id. Pure FE filter — the BFF guard
   * `/shared/:token/video-pipeline/*` is the security boundary.
   */
  pinnedVideoId?: string;
  /**
   * Controls whether per-slide download affordance is visible.
   * Shared viewers can set this to false for anonymous browsers.
   */
  canDownload?: boolean;
}

export default function VideoGalleryPanel({ token, readOnly: readOnlyProp, pinnedVideoId, canDownload = true }: VideoGalleryPanelProps = {}) {
  const v = useVideoGallery({ token, readOnly: readOnlyProp });
  const readOnly = v.readOnly;
  const [confirmDelete, setConfirmDelete] = useState<VideoFeedItem | null>(null);
  const [deleteError,   setDeleteError]   = useState<string | null>(null);
  // Bulk-purge modal visibility — opened from the header PurgeOrphansButton
  // when the operator wants to drop the unrecoverable orphan tail past TTL.
  const [purgeOpen, setPurgeOpen] = useState(false);
  // Sidecar backfill modal visibility — opened from the header button when
  // the operator wants to catch legacy videos up to the poster/preview path.
  const [backfillOpen, setBackfillOpen] = useState(false);

  // ── View mode (feed / gallery) ─────────────────────────────────────────
  // Hydrate from localStorage on first paint so the user lands on the
  // layout they last chose — same UX contract as YT Studio / IG.
  const [viewMode, setViewModeState] = useState<VideoViewMode>(() => readViewMode());
  const setViewMode = useCallback((mode: VideoViewMode) => {
    setViewModeState(mode);
    writeViewMode(mode);
  }, []);

  // Keyboard shortcuts (`f` → feed, `g` → gallery). Ignored while focus is
  // inside an editable element so we never steal keystrokes from real input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tgt = e.target as HTMLElement | null;
      if (tgt) {
        const tag = tgt.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tgt.isContentEditable) return;
      }
      if (e.key === 'f' || e.key === 'F') { setViewMode('feed');    return; }
      if (e.key === 'g' || e.key === 'G') { setViewMode('gallery'); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setViewMode]);

  const requestDelete = (it: VideoFeedItem) => {
    if (readOnly) return;
    setDeleteError(null);
    setConfirmDelete(it);
  };

  const confirmDeleteNow = async () => {
    const target = confirmDelete;
    if (!target) return;
    setConfirmDelete(null);
    try {
      await v.removeVideo(target.video_id);
    } catch (err) {
      setDeleteError((err as Error)?.message || 'Failed to delete video');
    }
  };

  // ── Shared header bits (used by both views) ───────────────────────────
  const headerControls = (
    <>
      <FreshnessDot freshness={v.freshness} isFetching={v.isFetching} />

      {/* Date-range bucket chips — horizontally scrollable, single row */}
      <div className="flex-1 min-w-0 overflow-x-auto scrollbar-none">
        <BucketChips
          buckets={v.buckets}
          activeKey={v.activeBucketKey}
          onSelect={(key) => {
            v.setSelectedDayMs(null);
            v.setActiveBucketKey(key);
          }}
        />
      </div>

      <CalendarJumpButton
        daysWithVideos={v.daysWithVideos}
        selectedDayMs={v.selectedDayMs}
        onSelect={v.setSelectedDayMs}
      />

      {/* Reconcile (self-heal) — admin shortcut that runs a bounded sweep
          over stuck media.video.* entities. Auto-fires once on first paint
          when real (not phantom) orphans are detected; this button lets
          the operator re-run on demand. Disabled while a sweep is in
          flight. */}
      {!readOnly && (
        <ReconcileButton
          isReconciling={v.isReconciling}
          lastSweep={v.lastSweep}
          sweepError={v.sweepError}
          onClick={() => v.reconcileSweep({ limit: 100 })}
        />
      )}

      {/* Purge orphans — operator-driven bulk delete for the unrecoverable
          tail past TTL. Distinct from Reconcile (which PROMOTES healable
          rows): this DELETES the failed/zombie/initiated-without-bytes
          rows that the GC cron would eventually clean up. Renders only
          when classification.orphan_count > 0 so the chrome stays minimal
          on healthy tenants. The button opens a confirm modal that
          requires typing the literal "PURGE ORPHANS" — the BFF re-checks
          the same literal (defence in depth). */}
      {!readOnly && (
        <PurgeOrphansButton
          orphanCount={v.classification.orphan_count}
          isPurging={v.isPurgingOrphans}
          lastPurge={v.lastPurge}
          purgeError={v.purgeError}
          onOpen={() => setPurgeOpen(true)}
        />
      )}

      {/* Sidecar backfill — operator-driven catch-up for older videos that
          predate the poster/preview pipeline. Uses the same dry-run-first,
          typed-literal confirm pattern as purge. */}
      {!readOnly && (
        <BackfillSidecarsButton
          isBackfilling={v.isBackfillingSidecars}
          lastBackfill={v.lastBackfill}
          backfillError={v.backfillError}
          onOpen={() => setBackfillOpen(true)}
        />
      )}

      {/* Phantom-population badge — visibility-only affordance.
          Surfaces orchestrator-side `upload.initiated` events that never
          produced GCS bytes. Hidden by default in gallery (mirrors the
          TikTok/IG/Shorts "no file = no row" contract) so this chip is
          the operator's only signal that they exist. Renders only when
          phantom_count > 0 so the chrome stays minimal on healthy
          tenants. Click toggles the developer view so the operator can
          inspect them. */}
      {!readOnly && (
        <PhantomBadge
          phantomCount={v.classification.phantom_count}
          includeInitiated={v.includeInitiated}
          onToggle={() => v.setIncludeInitiated(!v.includeInitiated)}
        />
      )}

      {!readOnly && <ShareLinkButton />}
    </>
  );

  // ── Per-video pin (shared-link `scope_key="video:<id>"`) ──────────────
  // When the panel is mounted from the per-video shared-link viewer we
  // filter the visible feed down to the single matching row and force
  // the immersive Feed layout (Gallery grid for one item would be
  // pointless chrome). The filter is FE-only — the BFF
  // `/shared/:token/video-pipeline/*` guards enforce the same scoping
  // server-side so a forged `videoId` cannot leak a sibling.
  const pinnedItems = useMemo(() => {
    if (!pinnedVideoId) return v.visibleItems;
    return v.visibleItems.filter((it) => it.video_id === pinnedVideoId);
  }, [pinnedVideoId, v.visibleItems]);

  const vForView = useMemo(
    () => (pinnedVideoId ? { ...v, visibleItems: pinnedItems } : v),
    [pinnedVideoId, pinnedItems, v],
  );

  const effectiveViewMode: VideoViewMode = pinnedVideoId ? 'feed' : viewMode;
  const isMinimalSingleVideoShared = Boolean(readOnly && pinnedVideoId);

  // ── Feed/error/empty body ─────────────────────────────────────────────
  const body = v.isLoading ? (
    <FeedSkeleton />
  ) : v.error && !vForView.visibleItems.length ? (
    isAuthErrorMessage(v.error.message) ? (
      <AuthDisconnectState message={v.error.message} />
    ) : (
      <FeedError message={v.error.message} onRetry={v.refresh} />
    )
  ) : vForView.visibleItems.length === 0 ? (
    <FeedEmpty total={v.total} />
  ) : effectiveViewMode === 'feed' ? (
    <VideoFeed
      v={vForView}
      onRequestDelete={requestDelete}
      readOnly={readOnly}
      canDownload={canDownload}
      minimalChrome={isMinimalSingleVideoShared}
    />
  ) : (
    <VideoGridView v={vForView} onRequestDelete={requestDelete} readOnly={readOnly} />
  );

  // ── Fullscreen toggle (feed mode) ───────────────────────────────
  // Escapes the surrounding SPA chrome so the feed becomes a true edge-to-
  // edge cinema surface. We use a CSS-only `fixed inset-0` fallback because
  // the browser Fullscreen API is unreliable in iframed/PWA contexts; the
  // result is identical visually and obeys the user's existing UI scaling.
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  // ── Render ─────────────────────────────────────────────────────────
  // Feed mode keeps the full-bleed black immersive layout — switcher is a
  // discreet header dropdown so nothing visually competes with playback.
  // Gallery mode adopts a standard SPA layout — visible tab strip up top.
  if (effectiveViewMode === 'feed') {
    if (isMinimalSingleVideoShared) {
      return (
        <div className="fixed inset-0 z-[100] w-full h-screen bg-black overflow-hidden">
          {body}
        </div>
      );
    }

    return (
      <div className={[
        'relative w-full bg-black overflow-hidden',
        fullscreen ? 'fixed inset-0 z-[100] rounded-none h-screen' : 'h-full rounded-xl',
      ].join(' ')}>

        {/* ── Gradient vignette behind the header ──────────────────── */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 z-20 h-24 pointer-events-none
                     bg-gradient-to-b from-black/80 via-black/40 to-transparent"
        />

        {/* ── Floating header overlay ──────────────────────────────── */}
        <header className="absolute inset-x-0 top-0 z-30 flex items-center gap-2 px-4 pt-3 pb-2">
          <span className="text-[13px] font-bold text-white/90 tracking-tight shrink-0 select-none">
            Videos
          </span>
          <ViewSwitcher mode={viewMode} onChange={setViewMode} variant="dropdown" />
          {headerControls}
          <FullscreenButton on={fullscreen} onToggle={() => setFullscreen((f) => !f)} />
        </header>

        {body}

        {!readOnly && <DeleteErrorToast error={deleteError} onDismiss={() => setDeleteError(null)} />}

        {!readOnly && confirmDelete && (
          <DeleteConfirmModal
            item={confirmDelete}
            onCancel={() => setConfirmDelete(null)}
            onConfirm={confirmDeleteNow}
          />
        )}

        {!readOnly && purgeOpen && (
          <PurgeOrphansModal
            orphanCount={v.classification.orphan_count}
            isPurging={v.isPurgingOrphans}
            lastPurge={v.lastPurge}
            purgeError={v.purgeError}
            onCancel={() => setPurgeOpen(false)}
            onPurge={(opts) => v.purgeOrphans(opts)}
          />
        )}

        {!readOnly && backfillOpen && (
          <BackfillSidecarsModal
            isBackfilling={v.isBackfillingSidecars}
            lastBackfill={v.lastBackfill}
            backfillError={v.backfillError}
            onCancel={() => setBackfillOpen(false)}
            onBackfill={(opts) => v.backfillSidecars(opts)}
          />
        )}
      </div>
    );
  }

  // Gallery mode — standard SPA: visible tab strip, padded body, scrolling
  // owned by the grid.
  return (
    <div className="relative h-full w-full bg-black overflow-hidden rounded-xl flex flex-col">
      <header className="shrink-0 flex items-center gap-2 px-4 pt-3 pb-2 border-b border-white/5 bg-black/60 backdrop-blur-md">
        <span className="text-[13px] font-bold text-white/90 tracking-tight shrink-0 select-none">
          Videos
        </span>
        <ViewSwitcher mode={viewMode} onChange={setViewMode} variant="tabs" />
        {headerControls}
      </header>

      <div className="flex-1 min-h-0">
        {body}
      </div>

      {!readOnly && <DeleteErrorToast error={deleteError} onDismiss={() => setDeleteError(null)} />}

      {!readOnly && confirmDelete && (
        <DeleteConfirmModal
          item={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={confirmDeleteNow}
        />
      )}

      {!readOnly && purgeOpen && (
        <PurgeOrphansModal
          orphanCount={v.classification.orphan_count}
          isPurging={v.isPurgingOrphans}
          lastPurge={v.lastPurge}
          purgeError={v.purgeError}
          onCancel={() => setPurgeOpen(false)}
          onPurge={(opts) => v.purgeOrphans(opts)}
        />
      )}

      {/* Sidecar backfill modal — mirror the feed-mode mount so the
          BackfillSidecarsButton in the gallery header actually opens a
          dialog (previously the click flipped `backfillOpen` to true but
          nothing rendered, producing the "nothing happens" complaint). */}
      {!readOnly && backfillOpen && (
        <BackfillSidecarsModal
          isBackfilling={v.isBackfillingSidecars}
          lastBackfill={v.lastBackfill}
          backfillError={v.backfillError}
          onCancel={() => setBackfillOpen(false)}
          onBackfill={(opts) => v.backfillSidecars(opts)}
        />
      )}
    </div>
  );
}

// ── View switcher (dropdown in feed mode, tab strip in gallery mode) ─────
//
// Two affordances, one source of truth. In feed mode the switcher must not
// dominate the player — it collapses to a quiet glass dropdown ("Feed ▾").
// In gallery mode the switcher is the primary navigation idiom of the
// page, so it expands into a segmented tab control sitting beside the
// title (YT Studio / IG-grid idiom).

function ViewSwitcher({
  mode,
  onChange,
  variant,
}: {
  mode: VideoViewMode;
  onChange: (m: VideoViewMode) => void;
  variant: 'dropdown' | 'tabs';
}) {
  if (variant === 'tabs') {
    return (
      <div
        role="tablist"
        aria-label="Video view"
        className="shrink-0 flex items-center rounded-lg border border-white/10 bg-white/[0.03] p-0.5"
      >
        <ViewTab active={mode === 'feed'}    onClick={() => onChange('feed')}    label="Feed"    hint="f" />
        <ViewTab active={mode === 'gallery'} onClick={() => onChange('gallery')} label="Gallery" hint="g" />
      </div>
    );
  }

  // Dropdown variant — feed mode. Native <select> for accessibility +
  // zero JS overhead; styled to match PAC Glass dark idiom.
  return (
    <label className="shrink-0 relative flex items-center">
      <span className="sr-only">Video view</span>
      <select
        value={mode}
        onChange={(e) => onChange(e.target.value as VideoViewMode)}
        className="appearance-none cursor-pointer
                   text-[11px] font-semibold uppercase tracking-wide
                   pl-2 pr-6 py-1 rounded-md
                   bg-black/60 border border-white/15 text-white/90
                   hover:border-white/30
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40"
        aria-label="Choose video view"
        title="Switch view (f: feed · g: gallery)"
      >
        <option value="feed"    className="bg-neutral-900">Feed</option>
        <option value="gallery" className="bg-neutral-900">Gallery</option>
      </select>
      {/* Chevron */}
      <svg
        viewBox="0 0 24 24"
        className="pointer-events-none absolute right-1.5 w-3 h-3 fill-white/70"
        aria-hidden
      >
        <path d="M7 10l5 5 5-5z" />
      </svg>
    </label>
  );
}

function ViewTab({
  active, onClick, label, hint,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        'text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md transition-colors duration-100',
        active
          ? 'bg-white/10 text-white border border-white/20'
          : 'text-neutral-400 hover:text-white border border-transparent',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40',
      ].join(' ')}
      title={`${label} view (shortcut: ${hint})`}
    >
      {label}
    </button>
  );
}

// ── Delete-error toast (shared) ───────────────────────────────────────────

function DeleteErrorToast({ error, onDismiss }: { error: string | null; onDismiss: () => void }) {
  if (!error) return null;
  return (
    <div
      role="status"
      className="fixed bottom-6 right-6 max-w-sm z-50
                 pac-glass-raised rounded-xl border border-rose-500/40
                 px-4 py-3 shadow-2xl shadow-black/60"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-rose-300 text-xs font-semibold">Delete failed</p>
          <p className="mt-0.5 text-neutral-400 text-[11px] break-words">{error}</p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="pac-glass-btn text-[11px] shrink-0"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ── Freshness dot ─────────────────────────────────────────────────────────

const FRESHNESS_DOT: Record<string, string> = {
  live:    'bg-emerald-400',
  cached:  'bg-sky-400',
  stale:   'bg-amber-400',
  offline: 'bg-rose-400',
  auth:    'bg-fuchsia-400',
};

function FreshnessDot({ freshness, isFetching }: { freshness: string; isFetching: boolean }) {
  const color = FRESHNESS_DOT[freshness] ?? FRESHNESS_DOT.cached;
  return (
    <span
      className={[
        'h-1.5 w-1.5 rounded-full shrink-0',
        color,
        isFetching ? 'animate-pulse' : '',
      ].join(' ')}
      aria-label={`Feed freshness: ${freshness}`}
      title={`Feed: ${freshness}${isFetching ? ' (updating)' : ''}`}
    />
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────

function FeedSkeleton() {
  return (
    <div
      className="h-full w-full flex items-center justify-center bg-black"
      aria-busy="true"
      aria-label="Loading videos"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-neutral-800 border-t-neon_orange-300 animate-spin" />
        <span className="text-[11px] text-neutral-500 tracking-wide">Loading videos…</span>
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────

function FeedEmpty({ total }: { total: number }) {
  return (
    <div className="h-full flex flex-col items-center justify-center bg-black gap-2 px-8 text-center">
      <p className="text-neutral-300 text-sm font-semibold">
        {total > 0 ? 'No videos in this period' : 'No videos yet'}
      </p>
      <p className="text-neutral-600 text-[11px] leading-relaxed">
        {total > 0
          ? 'Switch to a different date range using the chips above.'
          : 'Upload a video from PACMobile to get started.'}
      </p>
    </div>
  );
}

// ── Error state ───────────────────────────────────────────────────────────

function FeedError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="h-full flex flex-col items-center justify-center bg-black gap-3 px-8 text-center">
      <p className="text-rose-300 text-sm font-semibold">Couldn't load videos</p>
      <p className="text-neutral-500 text-[11px] break-words max-w-xs">{message}</p>
      <button type="button" onClick={onRetry} className="pac-glass-btn text-xs mt-2">
        Retry
      </button>
    </div>
  );
}

// ── Auth-disconnect state ─────────────────────────────────────────────────

function AuthDisconnectState({ message }: { message: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center bg-black gap-3 px-8 text-center">
      <p className="text-fuchsia-300 text-sm font-semibold">Session expired</p>
      <p className="text-neutral-500 text-[11px] break-words max-w-xs">{message}</p>
      <a href="/login" className="pac-glass-btn text-xs mt-2 inline-block">
        Sign in again
      </a>
    </div>
  );
}

// ── Single-item confirm-delete modal ──────────────────────────────────────
//
// Accessibility contract (M1):
//   • Surfaces ALL salient identity + lifecycle metadata so a screen-reader
//     user can verify what they are about to destroy without any visual
//     context. The dialog is announced via `aria-labelledby` (title) +
//     `aria-describedby` (full metadata block) so AT reads:
//
//       "Delete this video? <name>. Size N MB. Duration m:ss. Created Xh
//        ago. Source mobile. Status Sidecars written. Permanently removes…"
//
//   • Uses a semantic <dl> for the metadata block — both VoiceOver and
//     NVDA navigate definition lists term-by-term, which is exactly what
//     a non-sighted user needs to audit the target.
//
//   • `Enter` does NOT auto-confirm. A blind user pressing Return after
//     hearing the title would otherwise destroy the wrong asset. The
//     destructive action requires explicit Tab → Space/Enter on the
//     "Delete permanently" button. `Escape` still cancels.
//
//   • Initial focus is on Cancel (already the case) — keeps the dialog
//     safe-by-default per WCAG 3.3.4 "Error Prevention (Legal, Financial,
//     Data)".

function DeleteConfirmModal({
  item,
  onCancel,
  onConfirm,
}: {
  item: VideoFeedItem;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    // Only Escape — never Enter. See accessibility contract above:
    // auto-confirm on Enter is unsafe for blind operators who reach the
    // dialog mid-narration.
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onCancel]);

  // ── Derive a stable, human-readable metadata snapshot ─────────────────
  // Every field has a deterministic fallback ("Unknown") so screen readers
  // never encounter an empty <dd> (which AT skips silently and would leave
  // the user wondering whether the value was missing or just unread).
  const title = item.display_name?.trim()
    || item.filename?.trim()
    || null;

  const sizeText = item.byte_length > 0 ? formatBytes(item.byte_length) : 'Unknown';
  const durationText = item.duration_ms > 0 ? formatDuration(item.duration_ms) : 'Unknown';

  const createdMs = item.uploaded_at_ms || item.created_at_ms || 0;
  const createdRel = createdMs > 0 ? formatTimeAgo(createdMs) : 'Unknown';
  const createdAbs = createdMs > 0 ? new Date(createdMs).toLocaleString() : '';

  // Source provenance: orchestrator-canonical vs GCS-healed fallback. The
  // gallery surfaces this elsewhere as the freshness chip; the delete
  // dialog repeats it so a blind operator can hear "source: GCS fallback"
  // and pause if the row is mid-reconciliation.
  const sourceText = (() => {
    switch (item.source) {
      case 'gcs_fallback': return 'GCS fallback (orchestrator catching up)';
      case 'orch':
      case undefined:
      case null:           return 'Orchestrator (canonical)';
      default:             return String(item.source);
    }
  })();

  const statusLabel = statusTone(item.status).label;

  // The fully spoken description, assembled once and reused for both
  // aria-describedby and the visible metadata list. Keeping a single
  // string source ensures the visual and AT renderings can never drift.
  const descriptionPlain = [
    title ? `Title: ${title}.` : 'Title: metadata unavailable.',
    `Size: ${sizeText}.`,
    `Duration: ${durationText}.`,
    `Created: ${createdRel}${createdAbs ? ` (${createdAbs})` : ''}.`,
    `Source: ${sourceText}.`,
    `Status: ${statusLabel}.`,
    'Permanently removes the video, all sidecars (audio, transcript, subtitles, thumbnail), and run artifacts from object storage. Cannot be undone.',
  ].join(' ');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="del-title"
      aria-describedby="del-desc"
      onClick={onCancel}
    >
      <div
        className="pac-glass-raised rounded-xl w-full max-w-md p-5
                   border border-rose-500/40 shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="del-title" className="text-sm font-semibold text-rose-200">
          Delete this video?
        </h2>

        {/* Sole AT-readable description: a single coherent sentence the
            screen reader emits immediately after the title on dialog
            open. Visually hidden — the structured <dl> below renders the
            same data for sighted users with richer typography. */}
        <p id="del-desc" className="sr-only">{descriptionPlain}</p>

        {/* Visible structured metadata. <dl> is the correct semantic for
            key/value pairs; sighted users get the grid, AT users got the
            sentence above (the dl is aria-hidden to avoid double-reading). */}
        <dl
          aria-hidden="true"
          className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]"
        >
          <dt className="text-neutral-500">Title</dt>
          <dd className="text-neutral-100 font-medium break-words">
            {title ?? <span className="text-rose-300">⚠ Metadata unavailable</span>}
          </dd>

          <dt className="text-neutral-500">Size</dt>
          <dd className="text-neutral-200 tabular-nums">{sizeText}</dd>

          <dt className="text-neutral-500">Duration</dt>
          <dd className="text-neutral-200 tabular-nums">{durationText}</dd>

          <dt className="text-neutral-500">Created</dt>
          <dd className="text-neutral-200" title={createdAbs}>{createdRel}</dd>

          <dt className="text-neutral-500">Source</dt>
          <dd className="text-neutral-200">{sourceText}</dd>

          <dt className="text-neutral-500">Status</dt>
          <dd className="text-neutral-200">{statusLabel}</dd>
        </dl>

        <p className="mt-4 text-[11px] text-neutral-500 leading-relaxed">
          Permanently removes the video, all sidecars (audio, transcript, subtitles,
          thumbnail), and run artifacts from object storage. Cannot be undone.
        </p>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="pac-glass-btn text-xs" autoFocus>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            aria-label={`Delete ${title ?? 'video with missing metadata'} permanently, ${sizeText}, ${durationText}`}
            className="text-xs px-3 py-1.5 rounded border border-rose-500/60
                       bg-rose-500/20 text-rose-100 hover:bg-rose-500/30
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300/60"
          >
            Delete permanently
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ReconcileButton ────────────────────────────────────────────────────────
// Small admin shortcut in the gallery header. Triggers a server-side
// recovery sweep (POST /reconcile) that promotes stuck media.video.*
// entities (initiated-past-window with bytes, or uploaded-without-workflow)
// and re-triggers their workflow. Surfaces last-sweep summary as a tooltip.
//
// PAC Glass design — dark only, border-hover not bg-swap. Pulses while
// in-flight, flashes amber if the sweep failed (e.g. orchestrator down).
function ReconcileButton({
  isReconciling,
  lastSweep,
  sweepError,
  onClick,
}: {
  isReconciling: boolean;
  lastSweep: ReconcileSweepResponse | null;
  sweepError: string | null;
  onClick: () => void;
}) {
  // Build a compact tooltip summary of the last sweep outcome so an SRE can
  // hover and read e.g. "12 examined · 2 promoted · 1 restart · 9 healthy".
  const tooltip = sweepError
    ? `Last sweep failed: ${sweepError}`
    : lastSweep
      ? `Last sweep: ${lastSweep.examined} examined`
        + ` · ${(lastSweep.counts.promote_and_start ?? 0)} promoted`
        + ` · ${(lastSweep.counts.restart_workflow ?? 0)} restarted`
        + ` · ${(lastSweep.counts.skip_healthy ?? 0)} healthy`
        + ` · ${lastSweep.duration_ms}ms`
      : 'Run a recovery sweep over stuck uploads';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isReconciling}
      title={tooltip}
      aria-label="Reconcile stuck videos"
      className={[
        'shrink-0 inline-flex items-center gap-1.5',
        'px-2.5 py-1 rounded-md text-[11px] font-medium',
        'backdrop-blur-sm border transition-colors duration-150',
        isReconciling
          ? 'bg-amber-500/15 text-amber-200 border-amber-300/40 cursor-wait'
          : sweepError
            ? 'bg-rose-600/15 text-rose-200 border-rose-300/40 hover:border-rose-300/70'
            : 'bg-black/40 text-neutral-300 border-white/15 hover:border-neon_orange-300/60 hover:text-neutral-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40',
      ].join(' ')}
    >
      <svg
        viewBox="0 0 24 24"
        className={['w-3.5 h-3.5 fill-current', isReconciling ? 'animate-spin' : ''].join(' ')}
        aria-hidden
      >
        <path d="M12 4V1L7 6l5 5V7c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0020 13c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 004 13c0 4.42 3.58 8 8 8v3l5-5-5-5v3z" />
      </svg>
      <span>{isReconciling ? 'Reconciling…' : 'Reconcile'}</span>
    </button>
  );
}

// ── PhantomBadge ───────────────────────────────────────────────────────────
// Visibility chip for orchestrator-side `upload.initiated` events that
// never produced any GCS bytes ("phantoms"). Hidden from the default
// gallery by `canonicalizeVideoRows` (mirroring the TikTok / IG Reels /
// YouTube Shorts contract: "no file = no row") so this badge is the
// operator's only signal that they exist.
//
// Renders ONLY when phantom_count > 0 — the chrome stays minimal on
// healthy tenants. Clicking toggles `includeInitiated`, which surfaces
// the hidden population in the developer view so the operator can
// inspect or trigger manual cleanup. Active state inverts so the chip
// also serves as the "exit developer view" affordance.
//
// PAC Glass styling — dark only, neutral tone (this is informational,
// not an error), border-hover not bg-swap.
function PhantomBadge({
  phantomCount,
  includeInitiated,
  onToggle,
}: {
  phantomCount: number;
  includeInitiated: boolean;
  onToggle: () => void;
}) {
  if (phantomCount <= 0) return null;

  const label = includeInitiated
    ? `Hide ${phantomCount} phantom${phantomCount === 1 ? '' : 's'}`
    : `${phantomCount} phantom${phantomCount === 1 ? '' : 's'} hidden`;

  const tooltip = includeInitiated
    ? 'Currently surfacing orchestrator-side upload placeholders with no GCS bytes. Click to hide.'
    : `${phantomCount} orchestrator upload.initiated event${phantomCount === 1 ? '' : 's'} produced no file. Click to inspect.`;

  return (
    <button
      type="button"
      onClick={onToggle}
      title={tooltip}
      aria-label={tooltip}
      aria-pressed={includeInitiated}
      className={[
        'shrink-0 inline-flex items-center gap-1.5',
        'px-2.5 py-1 rounded-md text-[11px] font-medium',
        'backdrop-blur-sm border transition-colors duration-150',
        includeInitiated
          ? 'bg-neon_orange-500/15 text-neon_orange-200 border-neon_orange-300/40 hover:border-neon_orange-300/70'
          : 'bg-black/40 text-neutral-300 border-white/15 hover:border-neutral-200/40 hover:text-neutral-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40',
      ].join(' ')}
    >
      {/* Eye / eye-off iconography mirrors the toggle semantics. */}
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden>
        {includeInitiated ? (
          <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
        ) : (
          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
        )}
      </svg>
      <span>{label}</span>
    </button>
  );
}


// ── PurgeOrphansButton ─────────────────────────────────────────────────────
// Operator affordance to bulk-delete the unrecoverable orphan tail past
// TTL. Renders ONLY when `classification.orphan_count > 0` — the chrome
// stays minimal on healthy tenants.
//
// Distinct from Reconcile (which PROMOTES healable rows): this button
// opens a confirm modal that triggers DELETION of the failed / zombie /
// uploaded-without-workflow rows. The GC cron would eventually clean these
// up, but giving the operator an on-demand trigger removes the wait
// between scheduler runs.
//
// Healthy rows are PHYSICALLY un-deletable — the BFF decider gates on
// STATUS_SIDECARS_WRITTEN. No `force_delete_healthy` mode exists in the
// wire contract, so misuse is impossible at the data plane.
//
// PAC Glass — rose tone (destructive), border-hover not bg-swap, pulses
// while in-flight. Tooltip surfaces the last purge outcome for SREs.
function PurgeOrphansButton({
  orphanCount,
  isPurging,
  lastPurge,
  purgeError,
  onOpen,
}: {
  orphanCount: number;
  isPurging: boolean;
  lastPurge: PurgeOrphansResponse | null;
  purgeError: string | null;
  onOpen: () => void;
}) {
  if (orphanCount <= 0) return null;

  const tooltip = purgeError
    ? `Last purge failed: ${purgeError}`
    : lastPurge
      ? `Last purge: ${lastPurge.examined} examined`
        + ` · ${(lastPurge.counts.delete_scope_only ?? 0)} scope-only`
        + ` · ${(lastPurge.counts.delete_both ?? 0)} both`
        + ` · ${(lastPurge.counts.skip_young ?? 0)} young`
        + ` · ${lastPurge.duration_ms}ms${lastPurge.dry_run ? ' (dry-run)' : ''}`
      : `Bulk-delete ${orphanCount} orphan row${orphanCount === 1 ? '' : 's'} past TTL`;

  const label = isPurging
    ? 'Purging…'
    : `Clean up ${orphanCount} orphan${orphanCount === 1 ? '' : 's'}`;

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={isPurging}
      title={tooltip}
      aria-label={tooltip}
      className={[
        'shrink-0 inline-flex items-center gap-1.5',
        'px-2.5 py-1 rounded-md text-[11px] font-medium',
        'backdrop-blur-sm border transition-colors duration-150',
        isPurging
          ? 'bg-rose-500/15 text-rose-200 border-rose-300/40 cursor-wait'
          : purgeError
            ? 'bg-rose-600/20 text-rose-100 border-rose-300/50 hover:border-rose-300/70'
            : 'bg-rose-500/10 text-rose-200 border-rose-400/30 hover:border-rose-300/60 hover:text-rose-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300/40',
      ].join(' ')}
    >
      <svg viewBox="0 0 24 24" className={['w-3.5 h-3.5 fill-current', isPurging ? 'animate-pulse' : ''].join(' ')} aria-hidden>
        <path d="M6 19a2 2 0 002 2h8a2 2 0 002-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
      </svg>
      <span>{label}</span>
    </button>
  );
}


// ── PurgeOrphansModal ──────────────────────────────────────────────────────
// Two-gate destructive confirm:
//   1. Operator must type the literal "PURGE ORPHANS" (matches BFF gate).
//   2. Dry-run preview first — shows what WOULD be deleted before live.
//
// Healthy rows are physically un-deletable on the server (the decider gates
// on STATUS_SIDECARS_WRITTEN), so the only risk surface is wasted operator
// time on a no-op — the modal mitigates with the dry-run preview.
function PurgeOrphansModal({
  orphanCount,
  isPurging,
  lastPurge,
  purgeError,
  onCancel,
  onPurge,
}: {
  orphanCount: number;
  isPurging: boolean;
  lastPurge: PurgeOrphansResponse | null;
  purgeError: string | null;
  onCancel: () => void;
  onPurge: (opts: { confirm: string; dryRun?: boolean; limit?: number }) => Promise<PurgeOrphansResponse>;
}) {
  const [typed, setTyped] = useState('');
  const [preview, setPreview] = useState<PurgeOrphansResponse | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const confirmed = typed === PURGE_ORPHANS_CONFIRMATION;

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !isPurging) onCancel(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onCancel, isPurging]);

  const runDryRun = async () => {
    setLocalError(null);
    try {
      const res = await onPurge({ confirm: PURGE_ORPHANS_CONFIRMATION, dryRun: true, limit: 500 });
      setPreview(res);
    } catch (err) {
      setLocalError((err as Error)?.message || 'dry_run_failed');
    }
  };

  const runLive = async () => {
    if (!confirmed) return;
    setLocalError(null);
    try {
      await onPurge({ confirm: PURGE_ORPHANS_CONFIRMATION, dryRun: false, limit: 500 });
      onCancel();
    } catch (err) {
      setLocalError((err as Error)?.message || 'purge_failed');
    }
  };

  const summary = preview ?? lastPurge;
  const wouldDelete = summary
    ? (summary.counts.delete_scope_only ?? 0) + (summary.counts.delete_both ?? 0)
    : null;
  const displayError = localError || purgeError;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="purge-title"
      aria-describedby="purge-desc"
      onClick={() => { if (!isPurging) onCancel(); }}
    >
      <div
        className="pac-glass-raised rounded-xl w-full max-w-md p-5
                   border border-rose-500/40 shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="purge-title" className="text-sm font-semibold text-rose-200">
          Purge orphan rows?
        </h2>

        <p id="purge-desc" className="mt-2 text-[12px] text-neutral-300 leading-relaxed">
          Bulk-deletes up to <span className="font-medium text-neutral-100">{orphanCount}</span>{' '}
          orphan row{orphanCount === 1 ? '' : 's'} past TTL — failed uploads,
          zombie workflows, and uploaded-without-workflow rows. Healthy videos
          are physically un-deletable (server-side decider gates on terminal
          status). Run the dry-run preview first.
        </p>

        {summary && (
          <dl
            aria-label="Purge preview"
            className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]
                       border border-white/10 rounded p-2 bg-black/30"
          >
            <dt className="text-neutral-500">Examined</dt>
            <dd className="text-neutral-200 tabular-nums">{summary.examined}</dd>

            <dt className="text-neutral-500">Would delete</dt>
            <dd className="text-rose-200 tabular-nums font-medium">{wouldDelete}</dd>

            <dt className="text-neutral-500">Scope-only</dt>
            <dd className="text-neutral-200 tabular-nums">{summary.counts.delete_scope_only ?? 0}</dd>

            <dt className="text-neutral-500">Scope + bytes</dt>
            <dd className="text-neutral-200 tabular-nums">{summary.counts.delete_both ?? 0}</dd>

            <dt className="text-neutral-500">Healthy (skipped)</dt>
            <dd className="text-emerald-300 tabular-nums">{summary.counts.skip_healthy ?? 0}</dd>

            <dt className="text-neutral-500">In-flight (skipped)</dt>
            <dd className="text-neutral-200 tabular-nums">{summary.counts.skip_in_flight ?? 0}</dd>

            <dt className="text-neutral-500">Young (skipped)</dt>
            <dd className="text-neutral-200 tabular-nums">{summary.counts.skip_young ?? 0}</dd>

            <dt className="text-neutral-500">Mode</dt>
            <dd className="text-neutral-400">{summary.dry_run ? 'dry-run' : 'live'}</dd>
          </dl>
        )}

        <label className="mt-4 block">
          <span className="text-[11px] text-neutral-400">
            Type <code className="text-rose-200 font-mono">PURGE ORPHANS</code> to confirm
          </span>
          <input
            type="text"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={isPurging}
            className="mt-1 w-full px-2 py-1.5 rounded bg-black/50 border border-white/15
                       text-[12px] text-neutral-100 font-mono
                       focus:outline-none focus:border-rose-300/60"
            aria-label="Confirmation phrase"
          />
        </label>

        {displayError && (
          <p className="mt-2 text-[11px] text-rose-300" role="alert">
            {displayError}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPurging}
            className="pac-glass-btn text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={runDryRun}
            disabled={isPurging}
            className="text-xs px-3 py-1.5 rounded border border-white/20
                       bg-black/40 text-neutral-100 hover:border-neon_orange-300/60
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPurging && preview === null ? 'Previewing…' : 'Dry-run preview'}
          </button>
          <button
            type="button"
            onClick={runLive}
            disabled={!confirmed || isPurging}
            aria-label={`Purge ${wouldDelete ?? orphanCount} orphan rows permanently`}
            className="text-xs px-3 py-1.5 rounded border border-rose-500/60
                       bg-rose-500/20 text-rose-100 hover:bg-rose-500/30
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300/60
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isPurging ? 'Purging…' : 'Purge now'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── BackfillSidecarsButton ────────────────────────────────────────────────
// Operator affordance to re-run the existing faststart worker across the
// legacy catalog so poster + preview sidecars materialize for old videos.
function BackfillSidecarsButton({
  isBackfilling,
  lastBackfill,
  backfillError,
  onOpen,
}: {
  isBackfilling: boolean;
  lastBackfill: BackfillSidecarsResponse | null;
  backfillError: string | null;
  onOpen: () => void;
}) {
  const tooltip = backfillError
    ? `Last backfill failed: ${backfillError}`
    : lastBackfill
      ? `Last backfill: ${lastBackfill.examined} examined · ${lastBackfill.counts.eligible} eligible · ${lastBackfill.dry_run ? (lastBackfill.counts.would_dispatch ?? 0) + ' would dispatch' : lastBackfill.counts.dispatched + ' dispatched'} · ${lastBackfill.counts.errors} errors · ${lastBackfill.duration_ms}ms${lastBackfill.dry_run ? ' (dry-run)' : ''}`
      : 'Backfill poster and preview sidecars for legacy videos';

  const label = isBackfilling ? 'Backfilling…' : 'Backfill sidecars';

  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={isBackfilling}
      title={tooltip}
      aria-label={tooltip}
      className={[
        'shrink-0 inline-flex items-center gap-1.5',
        'px-2.5 py-1 rounded-md text-[11px] font-medium',
        'backdrop-blur-sm border transition-colors duration-150',
        isBackfilling
          ? 'bg-amber-500/15 text-amber-200 border-amber-300/40 cursor-wait'
          : backfillError
            ? 'bg-amber-600/20 text-amber-100 border-amber-300/50 hover:border-amber-300/70'
            : 'bg-amber-500/10 text-amber-200 border-amber-400/30 hover:border-amber-300/60 hover:text-amber-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/40',
      ].join(' ')}
    >
      <svg viewBox="0 0 24 24" className={['w-3.5 h-3.5 fill-current', isBackfilling ? 'animate-pulse' : ''].join(' ')} aria-hidden>
        <path d="M12 2L2 7l10 5 8-4V9H8v2H6V7l6-3 6 3v2l2-1V7L12 2zm0 7L2 14l10 5 10-5-10-5zm0 3.2l6.4 3.2L12 18.4 5.6 15.2 12 12.2z" />
      </svg>
      <span>{label}</span>
    </button>
  );
}

// ── BackfillSidecarsModal ─────────────────────────────────────────────────
// Dry-run-first catch-up modal. Mirrors purge's two-gate confirm flow but
// targets the non-destructive sidecar generation path.
//
// UX contract (operator visibility — DRY with the rest of the system):
//   • Idle      → dry-run preview button + confirm input + live button.
//   • Previewing→ shimmer state on the preview button only.
//   • Dispatching (live) → modal stays open, shimmer banner shows
//                 "Dispatching faststart workers…" with a live elapsed
//                 counter so the operator gets continuous feedback.
//   • Complete  → banner flips to "Backfill complete" with timing +
//                 the same summary <dl> reused (dispatched / errors /
//                 skipped) and a follow-up hint that sidecars finish
//                 asynchronously on the workers (~30s) — gallery auto-
//                 refresh is already wired via QueryClient invalidation
//                 inside useVideoGallery.backfillSidecarsAction.
//
// Reusing existing infrastructure (no new components):
//   - same <dl> grid pattern as PurgeOrphansModal.
//   - same QueryClient invalidation pipeline (live cache refresh).
//   - same `isBackfilling` flag as the global mutex.
function BackfillSidecarsModal({
  isBackfilling,
  lastBackfill,
  backfillError,
  onCancel,
  onBackfill,
}: {
  isBackfilling: boolean;
  lastBackfill: BackfillSidecarsResponse | null;
  backfillError: string | null;
  onCancel: () => void;
  onBackfill: (opts: { confirm: string; dryRun?: boolean; limit?: number }) => Promise<BackfillSidecarsResponse>;
}) {
  const [typed, setTyped] = useState('');
  const [preview, setPreview] = useState<BackfillSidecarsResponse | null>(null);
  // `liveResult` is non-null after a successful LIVE sweep — gates the modal
  // from auto-closing so the operator can read the completion summary
  // instead of seeing the modal vanish (the previous broken behaviour).
  const [liveResult, setLiveResult] = useState<BackfillSidecarsResponse | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  // Tick a wall-clock counter while a request is in flight so the operator
  // sees ongoing progress instead of a frozen "Backfilling…" string.
  const dispatchStartedAtRef = useRef<number | null>(null);
  const [, setElapsedTick] = useState(0);
  useEffect(() => {
    if (!isBackfilling) {
      dispatchStartedAtRef.current = null;
      return;
    }
    if (dispatchStartedAtRef.current == null) dispatchStartedAtRef.current = Date.now();
    const iv = setInterval(() => setElapsedTick((v) => v + 1), 250);
    return () => clearInterval(iv);
  }, [isBackfilling]);

  const confirmed = typed === BACKFILL_SIDECARS_CONFIRMATION;

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !isBackfilling) onCancel(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onCancel, isBackfilling]);

  const runDryRun = async () => {
    setLocalError(null);
    setLiveResult(null);
    try {
      const res = await onBackfill({ confirm: BACKFILL_SIDECARS_CONFIRMATION, dryRun: true, limit: 10000 });
      setPreview(res);
    } catch (err) {
      setLocalError((err as Error)?.message || 'dry_run_failed');
    }
  };

  const runLive = async () => {
    if (!confirmed) return;
    setLocalError(null);
    try {
      const res = await onBackfill({ confirm: BACKFILL_SIDECARS_CONFIRMATION, dryRun: false, limit: 10000 });
      // Keep the modal open so the operator can read the completion summary.
      setLiveResult(res);
      // Clear the dry-run preview so the summary <dl> below renders the LIVE
      // numbers (dispatched, errors, skipped) rather than the older preview.
      setPreview(null);
      // Reset typed confirmation so a follow-up live run requires re-typing
      // (prevents an accidental second sweep on the same modal open).
      setTyped('');
    } catch (err) {
      setLocalError((err as Error)?.message || 'backfill_failed');
    }
  };

  // Display precedence: live-result > fresh-preview > last-backfill-from-hook.
  const summary = liveResult ?? preview ?? lastBackfill;
  const isComplete = !!liveResult && !isBackfilling;
  const displayError = localError || backfillError;

  // Wall-clock elapsed while the request is in flight (250 ms granularity).
  const elapsedMs = dispatchStartedAtRef.current ? (Date.now() - dispatchStartedAtRef.current) : 0;
  const elapsedLabel = elapsedMs > 0
    ? `${(elapsedMs / 1000).toFixed(elapsedMs >= 10_000 ? 0 : 1)}s`
    : '—';

  // Status banner — three discrete states for max operator legibility.
  const banner = (() => {
    if (displayError && !isBackfilling) {
      return {
        tone: 'rose' as const,
        title: 'Backfill failed',
        detail: displayError,
      };
    }
    if (isBackfilling) {
      return {
        tone: 'amber' as const,
        title: liveResult || preview ? 'Re-running…' : 'Dispatching faststart workers…',
        detail: `Elapsed ${elapsedLabel} · sweeping the full library (up to 10 000 legacy videos)`,
      };
    }
    if (isComplete) {
      const r = liveResult!;
      return {
        tone: 'emerald' as const,
        title: 'Backfill complete',
        detail: `Dispatched ${r.counts.dispatched} of ${r.counts.eligible} eligible (examined ${r.examined}) in ${(r.duration_ms / 1000).toFixed(1)}s. Sidecars finish generating on the workers — posters and previews will appear in the gallery within ~30s.`,
      };
    }
    return null;
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="backfill-title"
      aria-describedby="backfill-desc"
      onClick={() => { if (!isBackfilling) onCancel(); }}
    >
      <div
        className="pac-glass-raised rounded-xl w-full max-w-md p-5 border border-amber-500/40 shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="backfill-title" className="text-sm font-semibold text-amber-200">
          {isComplete ? 'Sidecar backfill complete' : 'Backfill sidecars?'}
        </h2>

        {!isComplete && (
          <p id="backfill-desc" className="mt-2 text-[12px] text-neutral-300 leading-relaxed">
            Re-dispatches the existing faststart pipeline across legacy videos so
            poster JPEGs and muted preview clips can materialize for items that
            predate the current sidecar flow. Run the dry-run preview first, then
            type the literal confirmation to launch the live sweep.
          </p>
        )}

        {banner && (
          <div
            role={banner.tone === 'rose' ? 'alert' : 'status'}
            aria-live="polite"
            className={[
              'mt-3 rounded-md border px-3 py-2 text-[11px] leading-snug',
              banner.tone === 'amber'   ? 'border-amber-400/40 bg-amber-500/10 text-amber-100' : '',
              banner.tone === 'emerald' ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100' : '',
              banner.tone === 'rose'    ? 'border-rose-400/40 bg-rose-500/10 text-rose-100' : '',
            ].join(' ')}
          >
            <div className="flex items-center gap-2 font-medium">
              {banner.tone === 'amber' && (
                <span className="inline-block w-2 h-2 rounded-full bg-amber-300 animate-pulse" aria-hidden />
              )}
              {banner.tone === 'emerald' && (
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden>
                  <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
                </svg>
              )}
              <span>{banner.title}</span>
            </div>
            <div className="mt-1 text-[10.5px] opacity-80">{banner.detail}</div>
          </div>
        )}

        {summary && (
          <dl
            aria-label={isComplete ? 'Backfill result' : 'Backfill preview'}
            className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px] border border-white/10 rounded p-2 bg-black/30"
          >
            <dt className="text-neutral-500">Examined</dt>
            <dd className="text-neutral-200 tabular-nums">{summary.examined}</dd>

            <dt className="text-neutral-500">Eligible</dt>
            <dd className="text-amber-200 tabular-nums font-medium">{summary.counts.eligible}</dd>

            <dt className="text-neutral-500">{summary.dry_run ? 'Would dispatch' : 'Dispatched'}</dt>
            <dd className={['tabular-nums', summary.dry_run ? 'text-neutral-200' : 'text-emerald-200 font-medium'].join(' ')}>
              {summary.dry_run ? (summary.counts.would_dispatch ?? 0) : summary.counts.dispatched}
            </dd>

            <dt className="text-neutral-500">Skipped</dt>
            <dd className="text-neutral-200 tabular-nums">{summary.counts.skipped}</dd>

            <dt className="text-neutral-500">Errors</dt>
            <dd className={['tabular-nums', summary.counts.errors > 0 ? 'text-rose-300 font-medium' : 'text-neutral-200'].join(' ')}>
              {summary.counts.errors}
            </dd>

            <dt className="text-neutral-500">Mode</dt>
            <dd className="text-neutral-200 tabular-nums">{summary.dry_run ? 'dry-run' : 'live'}</dd>

            <dt className="text-neutral-500">Duration</dt>
            <dd className="text-neutral-200 tabular-nums">{(summary.duration_ms / 1000).toFixed(1)}s</dd>
          </dl>
        )}

        {!isComplete && (
          <label className="mt-4 block">
            <span className="text-[11px] text-neutral-400">
              Type <code className="text-amber-200 font-mono">BACKFILL SIDECARS</code> to confirm
            </span>
            <input
              type="text"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={isBackfilling}
              className="mt-1 w-full px-2 py-1.5 rounded bg-black/50 border border-white/15 text-[12px] text-neutral-100 font-mono focus:outline-none focus:border-amber-300/60"
              aria-label="Backfill confirmation phrase"
            />
          </label>
        )}

        {displayError && !banner && (
          <p className="mt-2 text-[11px] text-rose-300" role="alert">
            {displayError}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          {isComplete ? (
            <>
              <button
                type="button"
                onClick={() => { setLiveResult(null); }}
                disabled={isBackfilling}
                className="pac-glass-btn text-xs"
              >
                Run again
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="text-xs px-3 py-1.5 rounded border border-emerald-500/60 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onCancel}
                disabled={isBackfilling}
                className="pac-glass-btn text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runDryRun}
                disabled={isBackfilling}
                className="text-xs px-3 py-1.5 rounded border border-white/20 bg-black/40 text-neutral-100 hover:border-amber-300/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/40 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBackfilling && preview === null && !liveResult ? 'Previewing…' : 'Dry-run preview'}
              </button>
              <button
                type="button"
                onClick={runLive}
                disabled={!confirmed || isBackfilling}
                aria-label={`Backfill ${summary?.counts.dispatched ?? 0} videos' sidecars permanently`}
                className="text-xs px-3 py-1.5 rounded border border-amber-500/60 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isBackfilling ? 'Backfilling…' : 'Backfill now'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ── FullscreenButton ─────────────────────────────────────────────
// Glass icon toggle. When on, the panel becomes fixed-inset full-bleed.
function FullscreenButton({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={on ? "Exit fullscreen (Esc)" : "Fullscreen"}
      aria-label={on ? "Exit fullscreen" : "Enter fullscreen"}
      aria-pressed={on}
      className={[
        "shrink-0 inline-flex items-center justify-center",
        "w-8 h-8 rounded-full",
        "bg-black/50 hover:bg-black/70",
        "border border-white/15 hover:border-white/30",
        "text-white/85 backdrop-blur-md",
        "transition-colors duration-150",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-neon_orange-300/40",
      ].join(" ")}
    >
      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden>
        {on ? (
          <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
        ) : (
          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
        )}
      </svg>
    </button>
  );
}

// ShareGalleryButton has been extracted to `./ShareLinkButton` so the
// same component renders both the gallery-wide and per-video share
// affordances. See ShareLinkButton.tsx for the canonical implementation.
